// Classification Verification (Phase 3) — admin panel that drives the
// Perplexity-powered pharma vs commercial-partner audit. One-click start,
// live progress, and an audit log with per-row revert.
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Play, RefreshCw, RotateCcw, CheckCircle2, AlertCircle, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONF_COLOR = {
  high: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  medium: 'bg-amber-100 text-amber-800 border-amber-300',
  low: 'bg-slate-100 text-slate-700 border-slate-300',
};

export default function ClassificationVerifyPage() {
  const [status, setStatus] = useState(null);
  const [audit, setAudit] = useState([]);
  const [starting, setStarting] = useState(false);
  const [pollOn, setPollOn] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/verify-classifications/status`, { withCredentials: true });
      setStatus(r.data);
      if (!r.data?.running && pollOn) setPollOn(false);
    } catch (e) {
      console.error('status fetch failed', e);
    }
  }, [pollOn]);

  const fetchAudit = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/verify-classifications/audit`, { withCredentials: true });
      setAudit(r.data?.rows || []);
    } catch (e) {
      console.error('audit fetch failed', e);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchAudit();
  }, [fetchStatus, fetchAudit]);

  useEffect(() => {
    if (!pollOn) return undefined;
    const t = setInterval(() => {
      fetchStatus();
      fetchAudit();
    }, 4000);
    return () => clearInterval(t);
  }, [pollOn, fetchStatus, fetchAudit]);

  const startJob = async () => {
    if (!window.confirm(
      'Start the Perplexity classification audit?\n\n' +
      'Scope: every pharma + Commercial Partner profile in the CRM (~800).\n' +
      'Cost: ~$5 (one-off). Runtime: ~15 minutes in the background.\n\n' +
      'Auto-applies high/medium-confidence verdicts; logs every change for review.\n' +
      'Safe to run — every flip is fully revertible from the audit table below.'
    )) return;
    setStarting(true);
    try {
      const r = await axios.post(`${API}/admin/verify-classifications/start`, {}, { withCredentials: true });
      setStatus(r.data?.state);
      setPollOn(true);
      toast.success(r.data?.already_running ? 'Job already running — polling progress.' : 'Verification job started.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start');
    } finally {
      setStarting(false);
    }
  };

  const revertOne = async (auditId) => {
    if (!window.confirm('Revert this one flip back to its original category?')) return;
    try {
      const r = await axios.post(`${API}/admin/verify-classifications/${auditId}/revert`, {}, { withCredentials: true });
      toast.success(`Reverted to ${r.data?.reverted_to || 'previous'}`);
      await fetchAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Revert failed');
    }
  };

  const pct = status?.total ? Math.round((status.processed / status.total) * 100) : 0;
  const isDone = status && !status.running && status.processed > 0;

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="classification-verify-page">
      <h1 className="text-2xl font-bold text-slate-900 mb-2">Classification Verification</h1>
      <p className="text-sm text-slate-600 mb-6">
        Perplexity-powered audit that confirms every pharma vs Commercial Partner classification against the
        live web. High-confidence verdicts auto-apply; every change is logged and revertible.
      </p>

      {/* Job control panel */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Button
            onClick={startJob}
            disabled={starting || status?.running}
            data-testid="start-verification-btn"
            className="bg-violet-600 hover:bg-violet-700 text-white"
          >
            {status?.running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            {status?.running ? 'Running…' : 'Start verification'}
          </Button>
          <Button
            onClick={() => { fetchStatus(); fetchAudit(); }}
            variant="outline"
            size="sm"
            data-testid="refresh-status-btn"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
          {isDone && (
            <Badge variant="outline" className="border-emerald-300 text-emerald-700">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Last run complete
            </Badge>
          )}
        </div>

        {status?.total > 0 && (
          <div>
            <div className="flex justify-between text-xs text-slate-600 mb-1">
              <span>Progress: {status.processed} / {status.total} ({pct}%)</span>
              <span>
                Flipped: <b className="text-rose-700">{status.flipped}</b>{' '}
                · Kept: <b className="text-slate-700">{status.kept}</b>{' '}
                · Uncertain: <b className="text-amber-700">{status.uncertain}</b>{' '}
                · Errors: <b className="text-rose-600">{status.errors}</b>
              </span>
            </div>
            <div className="w-full h-2 bg-slate-100 rounded overflow-hidden">
              <div
                className="h-full bg-violet-500 transition-all"
                style={{ width: `${pct}%` }}
                data-testid="progress-bar"
              />
            </div>
            {status.last_error && (
              <p className="mt-2 text-[11px] text-rose-600 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Last error: {status.last_error}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Audit log table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900">Audit log — every flip applied</h2>
          <span className="text-xs text-slate-500">{audit.length} entries</span>
        </div>
        <div className="overflow-auto max-h-[60vh]">
          <table className="w-full text-xs" data-testid="audit-table">
            <thead className="bg-slate-50 text-slate-600 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Company</th>
                <th className="text-left px-3 py-2 font-semibold">From → To</th>
                <th className="text-left px-3 py-2 font-semibold">Confidence</th>
                <th className="text-left px-3 py-2 font-semibold">Rationale</th>
                <th className="text-left px-3 py-2 font-semibold">When</th>
                <th className="text-right px-3 py-2 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((row) => (
                <tr
                  key={row.id}
                  className={`border-t border-slate-100 ${row.reverted ? 'opacity-40 line-through' : ''}`}
                  data-testid={`audit-row-${row.id}`}
                >
                  <td className="px-3 py-2 font-medium text-slate-800">{row.company_name}</td>
                  <td className="px-3 py-2">
                    <span className="text-slate-500">{row.from_category}</span>
                    <span className="text-slate-400 mx-1">→</span>
                    <span className="font-semibold text-violet-700">{row.to_category}</span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded border text-[10px] font-medium ${CONF_COLOR[row.confidence] || CONF_COLOR.low}`}>
                      {row.confidence}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-600 max-w-md">
                    <span className="line-clamp-2">{row.rationale}</span>
                    {row.evidence_url && (
                      <a
                        href={row.evidence_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-0.5 ml-1 text-violet-600 hover:underline"
                      >
                        <ExternalLink className="w-2.5 h-2.5" /> src
                      </a>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-500 whitespace-nowrap">
                    {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {!row.reverted && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 text-[10px] border-slate-300"
                        onClick={() => revertOne(row.id)}
                        data-testid={`revert-row-${row.id}`}
                      >
                        <RotateCcw className="w-2.5 h-2.5 mr-1" /> Undo
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {audit.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-slate-400">
                    No flips yet. Run the verification to populate this log.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
